import React, { useState, useCallback } from 'react';
import { Modal, Box, Slider, Button, Stack, Typography, Divider } from '@mui/material';
import Cropper from 'react-easy-crop';

export default function ImageEditModal({ open, image, onClose, onDone }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [grayscale, setGrayscale] = useState(0);
  const [opacity, setOpacity] = useState(100);
  const [saturate, setSaturate] = useState(100);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const getCroppedImg = async () => {
    const img = new window.Image();
    img.src = image;
    await new Promise((resolve) => (img.onload = resolve));
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    ctx.filter = `
      brightness(${brightness}%)
      contrast(${contrast}%)
      grayscale(${grayscale}%)
      opacity(${opacity / 100})
      saturate(${saturate}%)
    `;

    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(
      img,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      -croppedAreaPixels.width / 2,
      -croppedAreaPixels.height / 2,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );
    ctx.restore();

    return canvas.toDataURL('image/jpeg');
  };

  const handleDone = async () => {
    const croppedImg = await getCroppedImg();
    onDone(croppedImg);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90vw',
          height: 696,
          bgcolor: '#fff',
          borderRadius: 2,
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden',
        }}
      >
        {/* Heading */}
        <Box sx={{ px: 4, pt: 3, pb: 2 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 600, color: '#00041B' , textAlign: 'start'}}>
            Edit Image
          </Typography>
        </Box>
        <Divider sx={{ bgcolor: '#e0e0e0', height: 2 }} />
        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', height: '100%' }}>
          {/* Left: Controls */}
          <Stack
            spacing={2}
            sx={{
              width: '50%',
              height: '100%',
              p: 4,
              boxSizing: 'border-box',
              justifyContent: 'center',
            }}
          >
            <div>Brightness</div>
            <Slider value={brightness} min={0} max={200} onChange={(_, v) => setBrightness(v)} />
            <div>Contrast</div>
            <Slider value={contrast} min={0} max={200} onChange={(_, v) => setContrast(v)} />
            <div>Grayscale</div>
            <Slider value={grayscale} min={0} max={100} onChange={(_, v) => setGrayscale(v)} />
            <div>Opacity</div>
            <Slider value={opacity} min={0} max={100} onChange={(_, v) => setOpacity(v)} />
            <div>Saturate</div>
            <Slider value={saturate} min={0} max={200} onChange={(_, v) => setSaturate(v)} />
            <div>Rotate</div>
            <Slider value={rotation} min={0} max={360} onChange={(_, v) => setRotation(v)} />
          </Stack>
          {/* Right: Cropper + Buttons */}
          <Box
            sx={{
              width: '50%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 4,
              boxSizing: 'border-box',
              position: 'relative',
            }}
          >
            <Box sx={{ width: '100%', flex: 1, position: 'relative', mb: 2 }}>
              <Cropper
                image={image}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={4 / 3}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                cropShape="rect"
                showGrid={true}
                style={{
                  containerStyle: { height: '100%' },
                  mediaStyle: {
                    filter: `
                      brightness(${brightness}%)
                      contrast(${contrast}%)
                      grayscale(${grayscale}%)
                      opacity(${opacity / 100})
                      saturate(${saturate}%)
                    `,
                  },
                }}
              />
            </Box>
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button variant="outlined" color="info" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="contained" color="info" onClick={handleDone}>
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}